import { NavigationTabs, type NavigationTabItem } from '@exam-management/components';
import { QuestionStatus } from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { usePermission } from '@shared/permissions';
import { useMemo } from 'react';

interface NavigationTabItemWithStatus extends NavigationTabItem {
  hiddenStatuses?: QuestionStatus[];
}

const TAB_ITEMS: NavigationTabItemWithStatus[] = [
  {
    value: 'details',
    label: 'Question Details',
    path: '/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId',
    requiredPermission: [APP_PERMISSIONS.QBANK.QUESTION.VIEW],
  },
  {
    value: 'usage',
    label: 'Question Usage',
    path: '/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId/question-usage',
    requiredPermission: [
      APP_PERMISSIONS.QBANK.STRUCTURE.VIEW,
      APP_PERMISSIONS.QBANK.QUESTION.VIEW,
      APP_PERMISSIONS.EXAM.EXAMINATION_COURSE.VIEW,
      APP_PERMISSIONS.EXAM.QSET.VIEW,
    ],
    hiddenStatuses: [
      QuestionStatus.InDevelopment,
      QuestionStatus.InReview,
      QuestionStatus.PreDraft,
    ],
  },
  {
    value: 'activity',
    label: 'Activity Log',
    path: '/qbank-structures/:structureId/qcontents/item-set/:questionId/:subQuestionId/activity-log',
    requiredPermission: [APP_PERMISSIONS.QBANK.QUESTION.VIEW, APP_PERMISSIONS.QBANK.AUDIT_LOG.VIEW],
    hiddenStatuses: [QuestionStatus.InDevelopment, QuestionStatus.InReview],
  },
];

interface ItemSetSubQuestionNavigationTabsProps {
  questionStatus: QuestionStatus;
}

export default function ItemSetSubQuestionNavigationTabs(
  props: Readonly<ItemSetSubQuestionNavigationTabsProps>,
) {
  const { questionStatus } = props;
  const { permissions } = usePermission();

  const tabList = useMemo(
    () =>
      TAB_ITEMS.filter((tab) => {
        const hasPermission =
          !tab.requiredPermission ||
          tab.requiredPermission.every((permission) => permissions.includes(permission));
        const isVisibleForStatus = !tab.hiddenStatuses?.includes(questionStatus);
        return hasPermission && isVisibleForStatus;
      }),
    [permissions, questionStatus],
  );

  return <NavigationTabs items={tabList} navigateOptions={{ replace: true }} />;
}
